import React, { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import './AdminRequests.css';

const API = import.meta.env.VITE_API_URL || 'https://backend-sable-five-58.vercel.app/api';

const getAuthHeaders = () => {
    const token = localStorage.getItem('authToken');
    return {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
};

type RequestStatus = 'pending' | 'code_sent' | 'verified' | 'rejected';

interface BizRequest {
    _id: string;
    userId: string;
    userEmail: string;
    userName: string;
    userPhone?: string;
    businessName: string;
    description?: string;
    phone?: string;
    address?: string;
    status: RequestStatus;
    verificationExpiry?: string;
    createdAt: string;
}

const STATUS_COLORS: Record<RequestStatus, string> = {
    pending:    '#f1c40f',
    code_sent:  '#3498db',
    verified:   '#2ecc71',
    rejected:   '#e74c3c',
};

const AdminRequests: React.FC = () => {
    const { language } = useLanguage();
    const T = (en: string, vi: string) => language === 'vi' ? vi : en;

    const [requests, setRequests] = useState<BizRequest[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [filterStatus, setFilterStatus] = useState<string>('');
    const [codeInputs, setCodeInputs] = useState<Record<string, string>>({});
    const [sending, setSending] = useState<Record<string, boolean>>({});
    const [feedback, setFeedback] = useState<Record<string, { type: 'success' | 'error'; text: string }>>({});

    const fetchRequests = useCallback(async () => {
        try {
            setError(null);
            const url = filterStatus
                ? `${API}/business-requests?status=${filterStatus}`
                : `${API}/business-requests`;
            const res = await fetch(url, { headers: getAuthHeaders() });
            const data = await res.json();
            if (data.success) {
                setRequests(data.data);
            } else {
                setError(data.message || T('Failed to load requests', 'Không thể tải danh sách'));
            }
        } catch {
            setError(T('Connection error', 'Lỗi kết nối'));
        } finally {
            setLoading(false);
        }
    }, [filterStatus]);

    useEffect(() => {
        fetchRequests();
        const interval = setInterval(fetchRequests, 10000);
        return () => clearInterval(interval);
    }, [fetchRequests]);

    const handleSendCode = async (id: string) => {
        const code = (codeInputs[id] || '').trim();
        if (!code) {
            setFeedback({ ...feedback, [id]: { type: 'error', text: T('Please enter a code', 'Vui lòng nhập mã') } });
            return;
        }
        setSending({ ...sending, [id]: true });
        setFeedback(prev => { const n = { ...prev }; delete n[id]; return n; });

        try {
            const res = await fetch(`${API}/business-requests/${id}/send-code`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify({ code }),
            });
            const data = await res.json();
            if (data.success) {
                setFeedback({ ...feedback, [id]: { type: 'success', text: T(`✅ Code sent! Expires in 2 minutes.`, `✅ Đã gửi mã! Hết hạn sau 2 phút.`) } });
                setCodeInputs(prev => { const n = { ...prev }; delete n[id]; return n; });
                fetchRequests();
            } else {
                setFeedback({ ...feedback, [id]: { type: 'error', text: data.message || T('Failed to send code', 'Gửi mã thất bại') } });
            }
        } catch {
            setFeedback({ ...feedback, [id]: { type: 'error', text: T('Connection error', 'Lỗi kết nối') } });
        } finally {
            setSending(prev => { const n = { ...prev }; delete n[id]; return n; });
        }
    };

    const handleReject = async (id: string) => {
        if (!window.confirm(T('Reject this request?', 'Từ chối yêu cầu này?'))) return;
        try {
            const res = await fetch(`${API}/business-requests/${id}/reject`, {
                method: 'PATCH',
                headers: getAuthHeaders(),
            });
            const data = await res.json();
            if (data.success) fetchRequests();
        } catch { /* silent */ }
    };

    const formatDate = (d: string) => new Date(d).toLocaleString(language === 'vi' ? 'vi-VN' : 'en-US');

    const statusLabel = (s: RequestStatus) => {
        const map: Record<RequestStatus, [string, string]> = {
            pending:   ['Pending', 'Đang chờ'],
            code_sent: ['Code Sent', 'Đã gửi mã'],
            verified:  ['Verified ✓', 'Đã xác nhận ✓'],
            rejected:  ['Rejected', 'Đã từ chối'],
        };
        return T(map[s][0], map[s][1]);
    };

    return (
        <div className="admin-requests-page">
            <div className="ar-header">
                <div>
                    <h1 className="ar-title">🏢 {T('Business Requests', 'Yêu cầu mở Business')}</h1>
                    <p className="ar-subtitle">
                        {T('Review and approve business registration requests from users.', 'Xem xét và phê duyệt các yêu cầu mở business từ người dùng.')}
                    </p>
                </div>
                <button className="ar-refresh-btn" onClick={fetchRequests} title={T('Refresh', 'Làm mới')}>
                    🔄 {T('Refresh', 'Làm mới')}
                </button>
            </div>

            {/* Filter */}
            <div className="ar-filter-bar">
                {(['', 'pending', 'code_sent', 'verified', 'rejected'] as const).map(s => (
                    <button
                        key={s}
                        className={`ar-filter-btn ${filterStatus === s ? 'active' : ''}`}
                        onClick={() => setFilterStatus(s)}
                    >
                        {s === '' ? T('All', 'Tất cả') : statusLabel(s as RequestStatus)}
                        {' '}
                        <span className="ar-count">
                            {s === '' ? requests.length : requests.filter(r => r.status === s).length}
                        </span>
                    </button>
                ))}
            </div>

            {error && <div className="ar-error">⚠️ {error}</div>}

            {loading ? (
                <div className="ar-loading">
                    <div className="ar-spinner" />
                    {T('Loading...', 'Đang tải...')}
                </div>
            ) : requests.length === 0 ? (
                <div className="ar-empty">
                    <div className="ar-empty-icon">📭</div>
                    <p>{T('No requests found.', 'Không có yêu cầu nào.')}</p>
                </div>
            ) : (
                <div className="ar-list">
                    {requests.map(req => (
                        <div key={req._id} className={`ar-card ar-card--${req.status}`}>
                            <div className="ar-card-top">
                                <div className="ar-card-info">
                                    <h3 className="ar-biz-name">🏢 {req.businessName}</h3>
                                    <div className="ar-user-info">
                                        <span>👤 {req.userName}</span>
                                        <span>✉️ {req.userEmail}</span>
                                        {req.userPhone && <span>📞 {req.userPhone}</span>}
                                    </div>
                                    {req.description && <p className="ar-desc">{req.description}</p>}
                                    <div className="ar-meta">
                                        {req.address && <span>📍 {req.address}</span>}
                                        {req.phone && <span>📞 {req.phone}</span>}
                                        <span>🕒 {formatDate(req.createdAt)}</span>
                                    </div>
                                </div>
                                <div className="ar-status-badge" style={{ background: STATUS_COLORS[req.status] + '22', color: STATUS_COLORS[req.status], border: `1px solid ${STATUS_COLORS[req.status]}55` }}>
                                    {statusLabel(req.status)}
                                    {req.status === 'code_sent' && req.verificationExpiry && (
                                        <span className="ar-expiry">
                                            {' · '}{T('expires', 'hết hạn')} {new Date(req.verificationExpiry).toLocaleTimeString()}
                                        </span>
                                    )}
                                </div>
                            </div>

                            {/* Actions — only for pending or code_sent */}
                            {(req.status === 'pending' || req.status === 'code_sent') && (
                                <div className="ar-actions">
                                    <div className="ar-send-code-row">
                                        <input
                                            type="text"
                                            className="ar-code-input"
                                            placeholder={T('Enter code to send (e.g. 4K8X92)', 'Nhập mã để gửi (vd: 4K8X92)')}
                                            value={codeInputs[req._id] || ''}
                                            onChange={e => setCodeInputs(prev => ({ ...prev, [req._id]: e.target.value }))}
                                            onKeyDown={e => e.key === 'Enter' && handleSendCode(req._id)}
                                            maxLength={20}
                                        />
                                        <button
                                            className="ar-send-btn"
                                            onClick={() => handleSendCode(req._id)}
                                            disabled={sending[req._id]}
                                        >
                                            {sending[req._id]
                                                ? T('Sending...', 'Đang gửi...')
                                                : T('Send Code 📧', 'Gửi mã 📧')}
                                        </button>
                                        <button
                                            className="ar-reject-btn"
                                            onClick={() => handleReject(req._id)}
                                        >
                                            {T('Reject', 'Từ chối')}
                                        </button>
                                    </div>
                                    {feedback[req._id] && (
                                        <div className={`ar-feedback ar-feedback--${feedback[req._id].type}`}>
                                            {feedback[req._id].text}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default AdminRequests;
